import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import {
  Sparkles, Ribbon, Bug, AlertTriangle, ShieldCheck, Droplet,
  Stethoscope, Shield, MessageCircleHeart, ChevronLeft, ExternalLink, HandHeart,
} from "lucide-react";
import { findTopic, DISCLAIMER, TOPICS } from "../data/healthTopics";

const ICON_MAP = {
  Sparkles, Ribbon, Bug, AlertTriangle, ShieldCheck, Droplet,
  Stethoscope, Shield, MessageCircleHeart,
};

const TONE_STYLES = {
  rose:   { chip: "bg-[#FFF0EC] text-[#C24C29] dark:bg-[#3A211B] dark:text-[#E07A5F]", bar: "bg-[#D95D39]" },
  red:    { chip: "bg-[#FDECEC] text-[#B4353A] dark:bg-[#3A1E20] dark:text-[#E88090]", bar: "bg-[#B4353A]" },
  amber:  { chip: "bg-[#FEF3D6] text-[#B45309] dark:bg-[#3A2E14] dark:text-[#E9B970]", bar: "bg-[#B45309]" },
  orange: { chip: "bg-[#FFEBDC] text-[#B45309] dark:bg-[#3A2517] dark:text-[#E8A377]", bar: "bg-[#D9723C]" },
  violet: { chip: "bg-[#EEE7FA] text-[#6D4AA3] dark:bg-[#2A2140] dark:text-[#B7A0E6]", bar: "bg-[#6D4AA3]" },
  yellow: { chip: "bg-[#FFF6D6] text-[#8A6A08] dark:bg-[#3A3216] dark:text-[#E1CB74]", bar: "bg-[#8A6A08]" },
  teal:   { chip: "bg-[#DDEFEA] text-[#20675A] dark:bg-[#1E322C] dark:text-[#7DC4B0]", bar: "bg-[#20675A]" },
  sage:   { chip: "bg-[#E4EDDE] text-[#3E6A2C] dark:bg-[#243019] dark:text-[#9ABF7F]", bar: "bg-[#3E6A2C]" },
};

function Section({ heading, body }) {
  return (
    <section className="mb-6 sm:mb-7">
      <h2 className="font-serif text-2xl sm:text-[26px] font-bold text-[#2C2623] dark:text-[#FAF5F0] tracking-tight mb-2">
        {heading}
      </h2>
      <p className="text-[15px] sm:text-base text-[#4A413B] dark:text-[#C7BDB4] leading-relaxed">
        {body}
      </p>
    </section>
  );
}

export default function HealthTopic() {
  const { slug } = useParams();
  const topic = findTopic(slug);
  if (!topic) return <Navigate to="/health" replace />;

  const Icon = ICON_MAP[topic.icon] || Sparkles;
  const tone = TONE_STYLES[topic.tone] || TONE_STYLES.rose;
  const related = TOPICS.filter((t) => t.slug !== topic.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#FFFDF9] dark:bg-[#1A1615]">
      <Navbar />
      <main
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
        data-testid={`health-topic-page-${topic.slug}`}
      >
        <Link
          to="/health"
          data-testid="health-topic-back-link"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#6E655F] hover:text-[#C24C29] dark:text-[#B5AAA1] dark:hover:text-[#E07A5F] transition-colors mb-6"
        >
          <ChevronLeft className="w-4 h-4" /> All health topics
        </Link>

        <header className="mb-8 sm:mb-10" data-testid="health-topic-hero">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest ${tone.chip}`}>
            <Icon className="w-3.5 h-3.5" /> {topic.kicker}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C2623] dark:text-[#FAF5F0] tracking-tight mt-3">
            {topic.title}
          </h1>
          <p className="text-base sm:text-lg text-[#6E655F] dark:text-[#B5AAA1] mt-3 leading-relaxed">
            {topic.subtitle}
          </p>
          <div className={`h-1 w-16 rounded-full ${tone.bar} mt-6`} />
        </header>

        <article
          className="prose-none"
          data-testid="health-topic-content"
        >
          {topic.sections.map((s, i) => (
            <Section key={i} heading={s.heading} body={s.body} />
          ))}
        </article>

        {topic.sources && topic.sources.length > 0 && (
          <Card
            className="mt-8 p-5 sm:p-6 rounded-3xl border-[#E8DEC8] bg-white dark:bg-[#221E1C] dark:border-[#423B36]"
            data-testid="health-topic-sources"
          >
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#8A7F78] dark:text-[#8F847C] mb-3">
              Trusted Canadian sources
            </div>
            <ul className="space-y-2">
              {topic.sources.map((src, i) => (
                <li key={i}>
                  <a
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`health-topic-source-${i}`}
                    className="inline-flex items-start gap-2 text-sm font-medium text-[#C24C29] dark:text-[#E07A5F] hover:underline"
                  >
                    <ExternalLink className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{src.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <div
          className="mt-8 p-5 sm:p-6 rounded-3xl border border-[#E8DEC8] bg-[#FFF9EE] dark:bg-[#221E1C] dark:border-[#423B36]"
          data-testid="health-topic-disclaimer"
        >
          <div className="flex items-start gap-3">
            <HandHeart className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
            <p className="text-sm text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">
              {DISCLAIMER}
            </p>
          </div>
        </div>

        <section className="mt-12" data-testid="health-topic-related">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-[#8A7F78] dark:text-[#8F847C] mb-3">
            Keep reading
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {related.map((r) => {
              const RIcon = ICON_MAP[r.icon] || Sparkles;
              const rt = TONE_STYLES[r.tone] || TONE_STYLES.rose;
              return (
                <Link
                  key={r.slug}
                  to={`/health/${r.slug}`}
                  data-testid={`health-topic-related-${r.slug}`}
                  className="group block p-4 rounded-2xl border border-[#E8DEC8] bg-white dark:bg-[#221E1C] dark:border-[#423B36] hover:border-[#D95D39] transition-colors"
                >
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${rt.chip} mb-2`}>
                    <RIcon className="w-4 h-4" />
                  </div>
                  <div className="font-serif font-bold text-[#2C2623] dark:text-[#FAF5F0] leading-tight">
                    {r.title}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}
